import {
  ActionList,
  ActionListItem,
  Text,
  type TourSteps,
} from "@prismicio/editor-ui"

import {
  trackTourStepAddContent,
  trackTourStepNewSlice,
} from "./analytics/tracking"

export const steps: TourSteps = [
  {
    selector: '[data-demo-id="section-editor"]',
    title: "Welcome to the Page Builder",
    content:
      "This is where you create and edit your pages. Pages are made of sections that we call “slices.”",
    onEntry: () => {
      const currentTab = new URL(window.location.href).searchParams.get(
        "section",
      )
      if (currentTab && currentTab !== "Main") {
        document
          .querySelector<HTMLElement>(
            '[data-demo-id="document-tab"][data-demo-tab-title="Main"]',
          )
          ?.click()
      } else {
        window.scroll({ top: 0, behavior: "smooth" })
      }
    },
  },
  {
    selector: '[data-demo-id="slice-table-container"]',
    title: "Real-time preview",
    content:
      "Observe your changes as they happen. The side panel provides a live preview of your slices for immediate feedback.",
  },
  {
    id: "add-a-new-slice",
    selector: '[data-demo-id="add-slice-button"]',
    title: "Add a new slice",
    content: (
      <>
        <Text>Now it’s your turn! Expand this page by adding a new slice.</Text>
        <Text>
          ⚡ Click the <strong>+</strong> button to add a slice.
        </Text>
      </>
    ),
    showNextButton: false,
    onEntry: () => {
      document
        .querySelector('[data-demo-id="slice-table-container"]')
        ?.scroll({ top: 0, behavior: "smooth" })
    },
  },
  {
    id: "choose-a-slice",
    selector: '[data-modal-id="select-slice-dialog"]',
    title: "Choose a slice",
    content: "⚡ Select a slice to continue.",
    showNextButton: false,
    entryDelay: 300,
    onEntry: ({ direction }) => {
      if (direction === "backward") {
        document
          .querySelector<HTMLElement>('[data-demo-id="add-slice-button"]')
          ?.click()
      }
    },
    onExit: ({ direction }) => {
      if (direction === "forward") {
        trackTourStepNewSlice()
      } else {
        document
          .querySelector<HTMLElement>('[data-demo-id="dialog-close-button"]')
          ?.click()
      }
    },
  },
  {
    selector: ['[data-modal-id="media"]', '[data-demo-id="section-editor"]'],
    title: "Add content",
    content: (
      <>
        <Text>
          You've added a slice to the page. Now it's time to fill it with
          content. Write anything that comes to mind!
        </Text>
        <Text>⚡ Click on a field and start writing.</Text>
      </>
    ),
    onExit: ({ direction }) => {
      if (direction === "forward") trackTourStepAddContent()
    },
  },
  {
    id: "save-your-content",
    selector: '[data-demo-id="save-button"]',
    title: "Save your content",
    content: (
      <>
        <Text>
          Once you're satisfied with your changes, it’s time to save them.
        </Text>
        <Text>
          ⚡ Click <strong>Save</strong>.
        </Text>
      </>
    ),
    padding: 4,
    showNextButton: false,
  },
  {
    id: "full-page-preview",
    selector: '[data-demo-id="preview-button"]',
    title: "Full page preview",
    content: (
      <>
        <Text>
          Now it’s time to see how your entire page looks like with the
          full-page preview.
        </Text>
        <Text>
          ⚡ Click <strong>Preview the page</strong>.
        </Text>
      </>
    ),
    showNextButton: false,
    exitDelay: 500,
  },
  {
    id: "reorder-your-slices-1",
    selector: '[data-demo-id="slice-table-container"]',
    title: "Reorder your slices",
    content: <ReorderSlicesContent currentStep={1} />,
    showNextButton: false,
    exitDelay: 1500,
  },
  {
    id: "reorder-your-slices-2",
    selector: '[data-demo-id="save-button"]',
    title: "Reorder your slices",
    content: <ReorderSlicesContent currentStep={2} />,
    padding: 4,
    showNextButton: false,
  },
  {
    id: "reorder-your-slices-3",
    selector: '[data-demo-id="preview-button"]',
    title: "Reorder your slices",
    content: <ReorderSlicesContent currentStep={3} />,
    exitDelay: 500,
  },
  {
    id: "publish-your-page",
    selector: '[data-demo-id="publish-button"]',
    title: "Publish your page",
    content: (
      <>
        <Text>
          Once your changes look good, publish them for everyone to see!
        </Text>
        <Text>
          ⚡ Click <strong>Publish</strong> to finish the tour.
        </Text>
      </>
    ),
    padding: 4,
  },
]

type ReorderSlicesContentProps = {
  /** 1-indexed step. */
  currentStep: number
}

function ReorderSlicesContent(props: ReorderSlicesContentProps) {
  const { currentStep } = props

  return (
    <ActionList>
      <ActionListItem
        completed={currentStep > 1}
        readOnly
        description="Drag your slice up or down to change the order."
      >
        1. Move a slice
      </ActionListItem>
      <ActionListItem
        completed={currentStep > 2}
        readOnly
        description={
          <>
            Click <strong>Save</strong>.
          </>
        }
      >
        2. Save the page
      </ActionListItem>
      <ActionListItem
        completed={currentStep > 3}
        readOnly
        description={
          <>
            Preview your changes again by clicking{" "}
            <strong>Preview the page</strong>.
          </>
        }
      >
        3. Preview
      </ActionListItem>
    </ActionList>
  )
}
